import React from 'react';
import {
  Pressable,
  type PressableProps,
  type PressableStateCallbackType,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

/**
 * `Pressable` с обязательным откликом на нажатие.
 *
 * <p><b>Зачем.</b> Голый RN-`Pressable` не даёт визуальной обратной связи вообще: в отличие от
 * `TouchableOpacity` он ничего не затемняет сам, а `android_ripple` надо просить руками. На
 * экране «Чаты» так и вышло — 69 кнопок и ни одного нажатого состояния: тап по строке чата,
 * по кнопке шапки, по чипу фильтра не менял на экране ничего до момента, пока не откроется
 * следующий экран. Между касанием и навигацией остаётся зазор в кадр-другой, и он читается
 * не как «сейчас откроется», а как «приложение зависло».
 *
 * <p><b>Почему затемнение, а не ripple.</b> Ripple рисуется прямоугольником по границам
 * компонента и вылезает за скруглённые углы, пока родителю не поставить `overflow: 'hidden'`.
 * Ставить его всем подряд нельзя: внутри нажимаемых областей списка лежат точки присутствия
 * и бейджи непрочитанного, которые нарочно свисают за край и были бы обрезаны. Прозрачность
 * работает одинаково на обеих платформах, ничего не клипует и не зависит от формы элемента.
 * Там, где ripple уже стоит осознанно (кнопки «удалить» и «уведомления» в строке чата), он
 * проходит насквозь через `rest` и продолжает работать — затемнение добавляется поверх.
 *
 * <p>`pressedStyle` позволяет заменить затемнение тонированной подложкой из палитры там, где
 * это уместнее; по умолчанию хватает прозрачности.
 */
export const PRESSED_OPACITY = 0.55;

const PRESSED_STYLE: ViewStyle = { opacity: PRESSED_OPACITY };

type AppPressableProps = PressableProps & {
  /** Чем заменить затемнение по умолчанию — например, тонированным фоном из темы. */
  pressedStyle?: StyleProp<ViewStyle>;
};

/**
 * Собрать итоговый стиль под состояние нажатия. Вынесено из компонента отдельной функцией
 * ради теста: рендерера в проекте нет (`@testing-library/react-native` не подключён), а
 * поведение здесь нетривиальное — `style` у `Pressable` бывает и объектом, и функцией от
 * состояния. Функциональную форму нужно сначала вызвать: те места, что уже красят себя по
 * `pressed` вручную, иначе потеряли бы свой стиль.
 */
export function resolvePressedStyle(
  style: AppPressableProps['style'],
  state: PressableStateCallbackType,
  pressedStyle?: StyleProp<ViewStyle>,
): StyleProp<ViewStyle> {
  const base = typeof style === 'function' ? style(state) : style;
  if (!state.pressed) return base;
  return [base, pressedStyle ?? PRESSED_STYLE];
}

export function AppPressable({ style, pressedStyle, ...rest }: AppPressableProps) {
  // children приходит внутри rest — в том числе в функциональной форме
  // (state) => ReactNode, которую нельзя пересобирать как JSX-детей.
  return (
    <Pressable {...rest} style={(state) => resolvePressedStyle(style, state, pressedStyle)} />
  );
}
